import React, { useState, useEffect, useMemo } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import Form from "react-bootstrap/form";
import moment from "moment";
import TextArea from "./TextArea";

const Input = React.forwardRef(
  (
    {
      name,
      label,
      error,
      onChange,
      comments,
      addComment,
      editComment,
      deleteComment,
      acknowledgeComment,
      commentsHandler,
      identical,
      type,
      value,
      defaultValue,
      className,
      style,
      tooltip,
      maxCharacters,
      subLabel,
      divClassName,
      ...rest
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState(
      value !== undefined
        ? value
        : defaultValue !== undefined
        ? defaultValue
        : ""
    );
    const [showComment, setShowComment] = useState(false);
    let fieldComments =
      commentsHandler !== undefined
        ? commentsHandler({ action: "GET", fieldName: name })
        : comments !== undefined
        ? comments.filter((comment) => comment.field._text === name)
        : [];

    useEffect(() => {
      if (value !== undefined && value !== null) setInputValue(value);
    }, [value]);

    const toggleComment = () => {
      setShowComment(!showComment);
    };

    const handleChange = (e) => {
      setInputValue(e.currentTarget.value);
      onChange(e);
    };

    const maxCharactersMarkup = useMemo(() => {
      if (!maxCharacters || !inputValue) {
        return null;
      }

      const textColor =
        inputValue.length >= maxCharacters
          ? "text-danger"
          : inputValue.length >= maxCharacters * 0.75
          ? "text-warning"
          : "text-black";

      return (
        <div className="clearfix">
          <div className={`float-end ${textColor}`}>
            {inputValue.length}/{maxCharacters}
          </div>
        </div>
      );
    }, [maxCharacters, inputValue]);

    return type === "date" ||
      type === "datetime-local" ||
      type === "time" ||
      type === "number" ? (
      <>
        <div className={divClassName || "mb-3"} {...rest}>
          {fieldComments.length > 0 && (
            <>
              {showComment &&
                fieldComments.map((comment, index) => (
                  <div
                    key={`${comment.field._text}${index}`}
                    className={`${
                      comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
                    } m-2 rounded d-print-none`}
                    title={
                      comment.acknowledgedby
                        ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                        : ""
                    }
                  >
                    <div className="p-2">
                      {comment.createdby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName && (
                        <>
                          <i
                            title="Delete Comment"
                            className="far fa-trash-alt text-white me-2"
                            onClick={() => {
                              confirmAlert({
                                title: "Delete Comment",
                                message:
                                  "Are you sure you want to delete this comment?",
                                buttons: [
                                  {
                                    label: "Yes",
                                    onClick: () => {
                                      commentsHandler !== undefined
                                        ? commentsHandler({
                                            action: "DELETE",
                                            comment: comment,
                                          })
                                        : deleteComment(comment);
                                    },
                                  },
                                  {
                                    label: "No",
                                    onClick: () => {
                                      /* do nothing */
                                    },
                                  },
                                ],
                              });
                            }}
                          ></i>
                          {!comment.acknowledgedby && (
                            <i
                              title="Edit Comment"
                              className="fas fa-pen text-white me-2"
                              onClick={(e) => {
                                commentsHandler !== undefined
                                  ? commentsHandler({
                                      action: "EDIT",
                                      comment: comment,
                                    })
                                  : editComment(comment);
                              }}
                            ></i>
                          )}
                        </>
                      )}
                      {commentsHandler !== undefined ||
                      acknowledgeComment !== undefined ? (
                        <>
                          {comment.acknowledgedby && (
                            <i
                              className="far fa-check-square text-white me-2"
                              onClick={() => {
                                if (
                                  comment.acknowledgedby._text ===
                                  JSON.parse(localStorage.getItem("safyInfo"))
                                    .user.displayName
                                )
                                  commentsHandler !== undefined
                                    ? commentsHandler({
                                        action: "ACKNOWLEDGE",
                                        comment: comment,
                                        acknowledge: false,
                                      })
                                    : acknowledgeComment(comment, false);
                              }}
                            ></i>
                          )}
                          {!comment.acknowledgedby && (
                            <i
                              className="far fa-square text-white me-2"
                              onClick={() => {
                                commentsHandler !== undefined
                                  ? commentsHandler({
                                      action: "ACKNOWLEDGE",
                                      comment: comment,
                                      acknowledge: true,
                                    })
                                  : acknowledgeComment(comment, true);
                              }}
                              title="Acknowledge Comment"
                            ></i>
                          )}
                        </>
                      ) : null}
                      <br />
                      <span className="h6">
                        {`${comment.createdby._text} - ${moment(
                          comment.datetime._text,
                          "MMMM DD YYYY hh:mm a"
                        ).format("L")} ${moment(
                          comment.datetime._text,
                          "MMMM DD YYYY hh:mm a"
                        ).format("LT")}`}
                      </span>
                      <br />
                      {comment.comment._text}
                    </div>
                  </div>
                ))}
              <i
                className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
                onClick={toggleComment}
              ></i>
            </>
          )}
          {identical && (
            <i className="fab fa-searchengin text-danger float-end mb-2 ms-2 d-print-none"></i>
          )}
          {label && (
            <label
              htmlFor={name}
              title={tooltip !== undefined ? tooltip : ""}
              className="form-label"
            >
              {label}{" "}
              {tooltip !== undefined ? (
                <i className="fas fa-info-circle"></i>
              ) : null}
            </label>
          )}
          <input
            {...rest}
            name={name}
            id={name}
            value={inputValue}
            defaultValue={defaultValue}
            ref={ref}
            className={className ? `${className} form-control` : "form-control"}
            onChange={handleChange}
            type={type}
            style={
              type === "date" || type === "time"
                ? style !== undefined
                  ? style
                  : { width: "200px" }
                : type === "datetime-local"
                ? {}
                : {}
            }
          />
          {subLabel ? (
            <label
              htmlFor={name}
              title={tooltip !== undefined ? tooltip : ""}
              className="form-label"
            >
              {subLabel}
            </label>
          ) : null}
          {maxCharactersMarkup}
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
      </>
    ) : (
      <TextArea
        name={name}
        label={label}
        value={inputValue}
        error={error}
        onChange={handleChange}
        comments={comments}
        addComment={addComment}
        editComment={editComment}
        deleteComment={deleteComment}
        acknowledgeComment={acknowledgeComment}
        commentsHandler={commentsHandler}
        identical={identical}
        style={style}
        tooltip={tooltip}
        maxCharacters={maxCharacters}
        ref={ref}
        subLabel={subLabel}
        divClassName={divClassName}
        {...rest}
      />
    );
  }
);

export default Input;
